import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type {
  AdminAccountDetail,
  AdminAccountListPage,
  AdminAccountListQuery,
  AdminAccountSummary,
} from '../types/admin';

const BASE = '/admin/v1/admins';

interface ListEnvelope {
  success: boolean;
  data?:
    | {
        items?: AdminAccountSummary[];
        content?: AdminAccountSummary[];
        totalElements?: number;
        totalPages?: number;
        number?: number;
        size?: number;
      }
    | AdminAccountSummary[]
    | null;
  meta?: { pagination?: { page: number; size: number; totalItems: number; totalPages: number } } | null;
}

interface DetailEnvelope {
  success: boolean;
  data?: AdminAccountDetail | null;
}

const buildQuery = (params: AdminAccountListQuery): string => {
  const q = new URLSearchParams();
  if (params.keyword) q.set('keyword', params.keyword);
  if (params.role) q.set('role', params.role);
  if (params.status) q.set('status', params.status);
  // FE state is 1-based; user-service Pageable is 0-based — mirror members list convention.
  q.set('page', String(Math.max(0, params.page - 1)));
  q.set('size', String(params.size));
  q.set('sort', 'createdAt');
  return q.toString();
};

export const adminAccountsQueryKey = (params: AdminAccountListQuery) =>
  ['admin', 'admins', 'list', params] as const;

export const adminAccountQueryKey = (adminUserId: string) =>
  ['admin', 'admins', adminUserId] as const;

export const useAdminAccountsQuery = (params: AdminAccountListQuery) =>
  useQuery<AdminAccountListPage>({
    queryKey: adminAccountsQueryKey(params),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?${buildQuery(params)}`);
      if (!res.ok) throw new Error(`admins fetch failed: ${res.status}`);
      const env = (await res.json()) as ListEnvelope;
      const d = env.data;
      const items = Array.isArray(d) ? d : (d?.items ?? d?.content ?? []);
      const page = Array.isArray(d) ? params.page - 1 : (d?.number ?? params.page - 1);
      const meta = env.meta?.pagination ?? {
        page,
        size: Array.isArray(d) ? params.size : (d?.size ?? params.size),
        totalItems: Array.isArray(d) ? items.length : (d?.totalElements ?? items.length),
        totalPages: Array.isArray(d) ? 1 : (d?.totalPages ?? 1),
      };
      return { items, meta };
    },
  });

export const useAdminAccountQuery = (adminUserId: string | null) =>
  useQuery<AdminAccountDetail>({
    queryKey: adminAccountQueryKey(adminUserId ?? ''),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}/${adminUserId}`);
      if (!res.ok) throw new Error(`admin detail fetch failed: ${res.status}`);
      const env = (await res.json()) as DetailEnvelope;
      if (!env.data) throw new Error('empty response');
      return env.data;
    },
    enabled: Boolean(adminUserId),
  });
